import { Button, Card, Form } from "@heroui/react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router";
import { Brand, PreferenceControls } from "../components/AppShell";
import { FieldStack, TextInput } from "../components/fields";
import { InlineError } from "../components/ui";
import { APP_NAME } from "../lib/config";
import { useLogin, useMe } from "../lib/queries";

export function LoginPage() {
  const { t } = useTranslation();
  const login = useLogin();
  const me = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  if (me.data) return <Navigate to={from} replace />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate({ email, password }, { onSuccess: () => navigate(from, { replace: true }) });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="flex items-center justify-between px-6 py-5 sm:px-10">
        <Brand />
        <PreferenceControls />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 pb-16">
        <div className="flex w-full max-w-[420px] flex-col gap-8">
          <div className="flex flex-col gap-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">{t("auth.title")}</h1>
            <p className="text-sm text-muted">{t("auth.subtitle", { app: APP_NAME })}</p>
          </div>

          <Card className="p-6 sm:p-8">
            <Form className="flex flex-col gap-6" onSubmit={submit}>
              <FieldStack>
                <TextInput
                  name="email"
                  type="email"
                  label={t("auth.email")}
                  placeholder={t("auth.emailPlaceholder")}
                  value={email}
                  onChange={setEmail}
                  autoComplete="username"
                  autoFocus
                  isRequired
                />
                <TextInput
                  name="password"
                  type="password"
                  label={t("auth.password")}
                  value={password}
                  onChange={setPassword}
                  autoComplete="current-password"
                  isRequired
                />
              </FieldStack>
              <InlineError error={login.error} />
              <Button type="submit" size="lg" fullWidth isPending={login.isPending}>
                {t("auth.submit")}
              </Button>
            </Form>
          </Card>

          <p className="text-center text-xs tracking-wide text-muted">{t("auth.channels")}</p>
        </div>
      </main>
    </div>
  );
}
